import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const axiosInstance = axios.create({
  baseURL: `${API_URL}/admin`,
  timeout: 30000,
  // Don't set a default Content-Type — let axios pick:
  //   plain object  -> application/json
  //   FormData      -> multipart/form-data; boundary=...
  // Forcing application/json here would break file uploads.
  headers: { Accept: 'application/json' },
});

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Map of cryptic Laravel field names to friendlier labels.
const FIELD_LABELS = {
  ref: 'Référence',
  slug: 'Identifiant URL',
  marque: 'Code marque',
  marque_nom: 'Marque',
  matiere: 'Matière',
  pv: 'Prix',
  old_price: 'Ancien prix',
  qntt: 'Stock',
  couleur: 'Couleur',
  verre: 'Verre',
  pont: 'Pont',
  branches: 'Branches',
  os: 'Système',
  form: 'Forme',
  genre: 'Genre',
  type: 'Type',
  name: 'Nom',
  email: 'Email',
  password: 'Mot de passe',
  password_confirmation: 'Confirmation du mot de passe',
  phone: 'Téléphone',
  image: 'Image',
  image_file: 'Image',
  pictures: 'Images',
  filter_query: 'Filtre',
  is_active: 'Statut actif',
  sort_order: 'Ordre',
  rating: 'Note',
  comment: 'Commentaire',
  customer_name: 'Nom du client',
  customer_email: 'Email du client',
  customer_phone: 'Téléphone du client',
  shipping_price: 'Frais de livraison',
  duration_minutes: 'Durée',
  slot_capacity: 'Capacité',
  price: 'Prix',
};

const labelize = (field) =>
  FIELD_LABELS[field] || field.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/**
 * Extract a human-readable message from any axios/server error.
 * Laravel 422 returns: { message, errors: { field: [msg, ...] } } — we
 * surface the actual field errors instead of the generic message.
 */
function buildUserMessage(error) {
  if (!error.response) {
    if (error.code === 'ECONNABORTED') return 'La requête a expiré, veuillez réessayer.';
    return 'Impossible de joindre le serveur. Vérifiez votre connexion.';
  }
  const { status, data } = error.response;

  if (status === 422 && data && typeof data === 'object' && data.errors) {
    const lines = [];
    for (const [field, messages] of Object.entries(data.errors)) {
      const list = Array.isArray(messages) ? messages : [messages];
      for (const m of list) lines.push(`${labelize(field)} : ${m}`);
    }
    if (lines.length) return lines.join('\n');
  }

  if (data && typeof data === 'object' && data.message) return data.message;

  if (status === 401) return 'Session expirée, veuillez vous reconnecter.';
  if (status === 403) return "Vous n'avez pas la permission d'effectuer cette action.";
  if (status === 404) return 'Élément introuvable.';
  if (status === 409) return 'Conflit : cet élément existe déjà.';
  if (status === 413) return 'Fichier trop volumineux.';
  if (status >= 500) return "Erreur serveur. Réessayez dans un instant.";
  return `Erreur (${status})`;
}

// Response interceptor: rewrite the error.message to something the user can read.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      // Don't redirect from /login (shows the inline error instead).
      if (!window.location.pathname.endsWith('/login')) {
        window.location.href = '/login';
      }
    }
    error.userMessage = buildUserMessage(error);
    error.fieldErrors = error.response?.data?.errors || null;
    return Promise.reject(error);
  }
);

export default axiosInstance;
